import { sanitizeRichTextHtml } from '../utils/sanitizeHtml';
﻿import React, { useEffect, useMemo, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import {
  Card,
  Button,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Typography,
  Tooltip,
  message,
  Spin,
  Tabs,
  Dropdown,
  Tag,
  Divider,
  Modal,
} from 'antd';
import {
  TranslationOutlined,
  ExportOutlined,
  SaveOutlined,
  BulbOutlined,
  FileImageOutlined,
  QuestionCircleOutlined,
  GlobalOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  DownloadOutlined,
  DownOutlined,
  SettingOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { getTargetLanguageFromPair, splitLanguagePair } from '../utils/languagePairs';
import DemoHeader from './DemoHeader';
import ReactQuill from 'react-quill';
import LoadingCard from '../components/LoadingCard';
import 'react-quill/dist/quill.snow.css';
import DemoHumanReviewModal from './DemoHumanReviewModal';
import DemoTranslationModal from './DemoTranslationModal';
import DemoExportBookModal from './DemoExportBookModal';
import DemoMetadataTranslationModal from './DemoMetadataTranslationModal';
import DemoTranslationOptionsModal, { TranslationOption } from './DemoTranslationOptionsModal';
import DemoKeywordSuggestionModal from './DemoKeywordSuggestionModal';
import DemoNeverTranslateModal from './DemoNeverTranslateModal';
import DemoAlwaysTranslateModal from './DemoAlwaysTranslateModal';

const { Title } = Typography;
const { TabPane } = Tabs;

type DemoBook = {
  title: string;
  author: string;
  series?: string;
  booknumber?: number;
  genres?: string[];
  tropes?: string[];
  keywords?: string[];
  blurb?: string;
  blurb_de?: string;
  blurb_fr?: string;
  coverUrl?: string;
  language?: string;
  uploadType?: string;
};

type DemoChapter = {
  chapter_guid: string;
  chapter_name: string;
  order: number;
  word_count?: number;
  original_html?: string;
};

const PageContainer = styled.div`
  background: var(--bg-app);
  padding: 1em;
  min-height: calc(100vh - 64px);

  @media (min-width: 768px) {
    padding: 2em;
  }
`;

const ContentWrap = styled.div`
  display: flex;
  gap: 12px;
  align-items: stretch;
`;

const Sidebar = styled.div`
  flex: 0 0 220px;
  min-width: 0;
  margin-right: 0px;
  padding: 0 2px 0px 2px;
`;

const SidebarHeader = styled.div`
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 10px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  color: var(--text-main);
`;

const ChaptersScroll = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
`;

const ChapterItem = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: left;
  justify-content: space-between;
  width: 100%;
  box-sizing: border-box;
  padding: 4px;
  margin: 3px 0;
  border-radius: 6px;
  cursor: pointer;
  background: ${(p) => (p.$active ? 'var(--surface-elevated)' : 'transparent')};
  font-size: 0.8em;

  &:hover {
    background: var(--surface-elevated);
  }
`;

const ChapterName = styled.span`
  flex: 1 1 auto;
  margin-right: 0px;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChapterMeta = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
`;

const MainPanel = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const MainWrap = styled.div`
  display: flex;
  flex-direction: column;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const TitleCenter = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;
`;

const TabCard = styled(Card)`
  background: var(--surface-base);
  border-radius: 8px;
  border: 1px solid var(--border-subtle);
  box-shadow: none;
  display: flex;
  flex-direction: column;
  height: 78vh;

  .ant-card-body {
    padding: 16px;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }
`;

const MetaContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
`;

const ScrollArea = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 12px;
  flex: 1 1 auto;
  min-height: 0;
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 16px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const LeftCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CoverImg = styled.img`
  width: 180px;
  height: 260px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid var(--border-subtle);
  margin-bottom: 12px;
`;

const ButtonsStack = styled.div`
  width: 180px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: 6px;
  margin-bottom: 12px;
`;

const CoverButtons = styled.div`
  width: 180px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 8px;
`;

const LanguageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding: 12px;
  background: var(--surface-elevated);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;

  .label {
    display: flex;
    align-items: center;
    margin-right: 12px;
    font-weight: 500;
  }
`;

const ActionsRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FormGrid3 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const FormGrid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const FooterBar = styled.div`
  border-top: 1px solid var(--border-subtle);
  padding-top: 12px;
  background: var(--surface-base);
  margin-top: 8px;
`;

const DemoBookDetailPage: React.FC = () => {
  const { bookguid } = useParams<{ bookguid: string }>();

  const [loading, setLoading] = useState(true);

  const [book, setBook] = useState<DemoBook | null>(null);
  const [chapters, setChapters] = useState<DemoChapter[]>([]);
  const [visibleChapters, setVisibleChapters] = useState<DemoChapter[]>([]);
  const [selectedChapterGuid, setSelectedChapterGuid] = useState<string | null>(null);
  const [loadingChapter, setLoadingChapter] = useState(false);
  const [chapterHtml, setChapterHtml] = useState<string>('');

  // Demo language picker state
  const [selectedLanguage, setSelectedLanguage] = useState<string>('original');
  const [activeTab, setActiveTab] = useState<string>('metadata');
  const [showMetadataPanel, setShowMetadataPanel] = useState<boolean>(true);
  const [humanReviewModalVisible, setHumanReviewModalVisible] = useState(false);
  const [isTranslateModalVisible, setIsTranslateModalVisible] = useState(false);
  const [isExportModalVisible, setIsExportModalVisible] = useState(false);
  const [resetCounter, setResetCounter] = useState(0);
  // Demo keyword suggestion state
  const [isKeywordModalVisible, setIsKeywordModalVisible] = useState(false);
  const [isSuggestingKeywords, setIsSuggestingKeywords] = useState(false);
  const [suggestedKeywordsReady, setSuggestedKeywordsReady] = useState<{ [lang: string]: boolean }>({});

  // Demo metadata translation state
  const [isMetaModalVisible, setIsMetaModalVisible] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translateMeta, setTranslateMeta] = useState<{ title: boolean; series: boolean; blurb: boolean }>({
    title: false,
    series: false,
    blurb: false,
  });
  const [targetLangMeta, setTargetLangMeta] = useState<string>('');
  const [quotationStyle, setQuotationStyle] = useState<string>('default');
  const [demoTranslated, setDemoTranslated] = useState<{ [lang: string]: { title?: string; series?: string; blurb?: string } }>({});
  // Demo: per-language chapter translation flags (by chapter order)
  const [translatedChapters, setTranslatedChapters] = useState<Record<string, number[]>>({});
const [isTitleOptionsVisible, setIsTitleOptionsVisible] = useState(false);
const [titleOptions, setTitleOptions] = useState<TranslationOption[]>([]);

// Inline edit state for translated metadata (persists until Reset)
const [isEditingTranslatedTitle, setIsEditingTranslatedTitle] = useState(false);
const [editedTranslatedTitle, setEditedTranslatedTitle] = useState('');
const [isEditingTranslatedSeries, setIsEditingTranslatedSeries] = useState(false);
const [editedTranslatedSeries, setEditedTranslatedSeries] = useState('');
const [isEditingTranslatedBlurb, setIsEditingTranslatedBlurb] = useState(false);
const [editedTranslatedBlurb, setEditedTranslatedBlurb] = useState('');

// Demo never/always translate
const [isNeverModalVisible, setIsNeverModalVisible] = useState(false);
const [isAlwaysModalVisible, setIsAlwaysModalVisible] = useState(false);
const [currentLanguagePair, setCurrentLanguagePair] = useState<string>('');
const [neverWords, setNeverWords] = useState<{ [pair: string]: string[] }>({});
const [alwaysWords, setAlwaysWords] = useState<{ [pair: string]: string[] }>({});

  const orderedChapters = useMemo(
    () => [...visibleChapters].sort((a, b) => a.order - b.order),
    [visibleChapters]
  );

  const totalWords = useMemo(
    () => visibleChapters.reduce((sum, ch) => sum + (typeof ch.word_count === 'number' ? ch.word_count : 0), 0),
    [visibleChapters]
  );

  const selectedChapter = useMemo(
    () => orderedChapters.find((c) => c.chapter_guid === selectedChapterGuid) || null,
    [orderedChapters, selectedChapterGuid]
  );

  const extractBody = useCallback((html: string) => {
    const m = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    return m ? m[1] : html;
  }, []);

  const loadChapter = useCallback(async (chapter: DemoChapter) => {
    if (!bookguid || !chapter) {
      setChapterHtml('');
      return;
    }
    setLoadingChapter(true);
    try {
      const langFolder = (() => {
        if (!selectedLanguage || selectedLanguage === 'original') return 'Original';
        return getTargetLanguageFromPair(selectedLanguage) || 'Original';
      })();

      // Gate translated languages until user has "translated" the chapter in demo
      const isTranslatedViewLocal = selectedLanguage !== 'original';
      const targetLang = isTranslatedViewLocal ? (getTargetLanguageFromPair(selectedLanguage) || '').trim() : '';
      if (isTranslatedViewLocal && targetLang) {
        const done = new Set((translatedChapters[targetLang] || []));
        if (!done.has(chapter.order)) {
          setChapterHtml('<div style="color:#8c8c8c">This Chapter has not yet been translated.</div>');
          setLoadingChapter(false);
          return;
        }
      }

      const basenameFromOriginal = (() => {
        const p = (chapter as any).original_html as string | undefined;
        if (!p) return '';
        const idx = p.lastIndexOf('/')
        return idx >= 0 ? p.slice(idx + 1) : p;
      })();

      const order = chapter.order;
      const pad = (n: number, len: number) => String(n).padStart(len, '0');

      // Generate candidate filenames in priority order
      const baseCandidates: string[] = [];
      if (basenameFromOriginal) baseCandidates.push(basenameFromOriginal);
      baseCandidates.push(
        `Section${pad(order, 4)}.html`,
        `Section${pad(order, 3)}.html`,
        `timemachine_ch${order}.html`,
        `chapter${order}.html`
      );

      const pathCandidates: string[] = [];
      for (const base of baseCandidates) {
        // Prefer language folder
        pathCandidates.push(`/demo/books/${bookguid}/${langFolder}/${base}`);
      }
      for (const base of baseCandidates) {
        // Fallback to book root (in case files are placed there)
        pathCandidates.push(`/demo/books/${bookguid}/${base}`);
      }
      // As a final fallback, try original path if provided (legacy)
      if ((chapter as any).original_html) {
        pathCandidates.push(`/demo/books/${bookguid}/${(chapter as any).original_html}`);
      }

      let loaded = false;
      for (const url of pathCandidates) {
        try {
          const res = await fetch(url, { cache: 'no-store' });
          if (!res.ok) continue;
          const raw = await res.text();
          setChapterHtml(extractBody(raw));
          loaded = true;
          break;
        } catch {
          // try next
        }
      }

      if (!loaded) throw new Error('No matching chapter HTML found');
    } catch (e) {
      console.error(e);
      setChapterHtml('<div style="color:#cc0000">Failed to load chapter content.</div>');
    } finally {
      setLoadingChapter(false);
    }
  }, [bookguid, extractBody, selectedLanguage, translatedChapters]);

  // Reload chapter when language, selection, or list changes
  useEffect(() => {
    if (selectedChapterGuid) {
      const ch = orderedChapters.find(c => c.chapter_guid === selectedChapterGuid);
      if (ch) void loadChapter(ch);
    }
  }, [selectedLanguage, selectedChapterGuid, orderedChapters, loadChapter]);

  const loadData = useCallback(async () => {
    if (!bookguid) return;
    setLoading(true);
    try {
      const [bookRes, chRes] = await Promise.all([
        fetch(`/demo/books/${bookguid}/book.json`),
        fetch(`/demo/books/${bookguid}/chapters.json`),
      ]);
      if (!bookRes.ok) throw new Error('Failed to load book.json');
      if (!chRes.ok) throw new Error('Failed to load chapters.json');

      const bookJson: DemoBook = await bookRes.json();
      const chaptersJson: DemoChapter[] = await chRes.json();

      const normalizedBook: DemoBook = {
        ...bookJson,
        genres: bookJson.genres || [],
        tropes: bookJson.tropes || [],
        keywords: bookJson.keywords || [],
      };

      setBook(normalizedBook);
      setChapters(chaptersJson || []);
      setVisibleChapters(chaptersJson || []);
      setSelectedChapterGuid(null);
    } catch (e) {
      console.error(e);
      message.error('Failed to load demo data');
    } finally {
      setLoading(false);
    }
  }, [bookguid]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Build chapter list strictly from files present under the selected language folder
  useEffect(() => {
    const langFolder = (() => {
      if (!selectedLanguage || selectedLanguage === 'original') return 'Original';
      return getTargetLanguageFromPair(selectedLanguage) || 'Original';
    })();

    let cancelled = false;

    const buildFromFiles = async () => {
      if (!bookguid) return;

      const wasChapterView = selectedChapterGuid !== null && !showMetadataPanel;
      const prevOrder = wasChapterView
        ? (orderedChapters.find(c => c.chapter_guid === selectedChapterGuid)?.order ?? null)
        : null;

      const chaptersFromFiles: DemoChapter[] = [];

      // Helper: prefer canonical names from the root chapters.json
      const nameForOrder = (order: number, fallback: string) => {
        const canonical = chapters.find(c => c.order === order)?.chapter_name;
        return canonical && canonical.trim() ? canonical : fallback;
      };

      // Prefer an explicit index when available
      try {
        const idxRes = await fetch(`/demo/books/${bookguid}/${langFolder}/chapters.json`, { cache: 'no-store' });
        if (idxRes.ok) {
          const data = await idxRes.json();
          const toEntry = (entry: any, i: number): DemoChapter | null => {
            if (!entry) return null;
            if (typeof entry === 'string') {
              const file = entry;
              const order = (() => {
                const m = file.match(/Section(\d+)\.html$/i) || file.match(/ch(\d+)\.html$/i) || file.match(/_(\d+)\.html$/i);
                return m ? parseInt(m[1], 10) : i;
              })();
              const fallback = /title/i.test(file) ? 'Title Page' : `Chapter ${order}`;
              const name = nameForOrder(order, fallback);
              return { chapter_guid: `${langFolder.toLowerCase()}-ch${order}`, chapter_name: name, order, word_count: 0, original_html: `${langFolder}/${file}` };
            }
            if (typeof entry === 'object') {
              const file = String(entry.file || '');
              if (!file) return null;
              const order = typeof entry.order === 'number' ? entry.order : (i as number);
              const fallback = /title/i.test(file) ? 'Title Page' : `Chapter ${order}`;
              const name = nameForOrder(order, entry.name || fallback);
              return { chapter_guid: `${langFolder.toLowerCase()}-ch${order}`, chapter_name: name, order, word_count: 0, original_html: `${langFolder}/${file}` };
            }
            return null;
          };
          const list: DemoChapter[] = Array.isArray(data) ? data.map(toEntry).filter(Boolean) as DemoChapter[] : [];
          if (list.length > 0) {
            if (!cancelled) {
              setVisibleChapters(list);
              if (wasChapterView) {
                let target: DemoChapter | undefined;
                if (prevOrder != null) target = list.find(c => c.order === prevOrder);
                if (!target) target = list[0];
                if (target) {
                  if (target.chapter_guid !== selectedChapterGuid) {
                    setSelectedChapterGuid(target.chapter_guid);
                  }
                  setShowMetadataPanel(false);
                } else {
                  setSelectedChapterGuid(null);
                }
              }
            }
            return; // Use index result; skip probing
          }
        }
      } catch {
        // Fall through to probing
      }

      const existsOk = async (p: string): Promise<boolean> => {
        try {
          const res = await fetch(p, { method: 'GET', cache: 'no-store' });
          return res.ok;
        } catch {
          return false;
        }
      };

      const pushChapter = (order: number, name: string, file: string) => {
        chaptersFromFiles.push({
          chapter_guid: `${langFolder.toLowerCase()}-ch${order}`,
          chapter_name: nameForOrder(order, name),
          order,
          word_count: 0,
          original_html: `${langFolder}/${file}`,
        });
      };

      if (bookguid === 'demo-book-flatland') {
        // Title page if present
        if (await existsOk(`/demo/books/${bookguid}/${langFolder}/TitlePage.html`)) {
          pushChapter(0, 'Title Page', 'TitlePage.html');
        }
        // Sections 1..22
        for (let i = 1; i <= 22; i++) {
          const fname = `Section${String(i).padStart(4, '0')}.html`;
          if (await existsOk(`/demo/books/${bookguid}/${langFolder}/${fname}`)) {
            pushChapter(i, `Chapter ${i}`, fname);
          }
        }
      } else if (bookguid === 'demo-book-time-machine') {
        // Exactly 17 chapters
        for (let i = 1; i <= 17; i++) {
          const fname = `timemachine_ch${i}.html`;
          if (await existsOk(`/demo/books/${bookguid}/${langFolder}/${fname}`)) {
            pushChapter(i, `Chapter ${i}`, fname);
          }
        }
      } else {
        // Fallback: try common patterns up to 30
        for (let i = 1; i <= 30; i++) {
          const candidates = [
            `timemachine_ch${i}.html`,
            `Section${String(i).padStart(4, '0')}.html`,
            `chapter${i}.html`,
          ];
          for (const fname of candidates) {
            const url = `/demo/books/${bookguid}/${langFolder}/${fname}`;
            if (await existsOk(url)) {
              pushChapter(i, `Chapter ${i}`, fname);
              break;
            }
          }
        }
      }

      if (!cancelled) {
        // Only override if we actually detected chapters for this language
        if (chaptersFromFiles.length > 0) {
          setVisibleChapters(chaptersFromFiles);
          if (wasChapterView) {
            let target: DemoChapter | undefined;
            if (prevOrder != null) {
              target = chaptersFromFiles.find(c => c.order === prevOrder);
            }
            if (!target) target = chaptersFromFiles[0];
            if (target) {
              if (target.chapter_guid !== selectedChapterGuid) {
                setSelectedChapterGuid(target.chapter_guid);
              }
              setShowMetadataPanel(false);
            } else {
              setSelectedChapterGuid(null);
            }
          }
        }
      }
    };

    // Keep existing list visible while rebuilding; update only when we find chapters
    buildFromFiles();

    return () => {
      cancelled = true;
    };
  }, [selectedLanguage, bookguid, chapters]);

  const [form] = Form.useForm();

  useEffect(() => {
    if (book) {
      form.setFieldsValue({
        title: book.title,
        author: book.author,
        series: book.series || '',
        booknumber: book.booknumber ?? 1,
        language: book.language || '',
        blurb: book.blurb || '',
        genres: book.genres || [],
        tropes: book.tropes || [],
        keywords: book.keywords || [],
      });
    }
  }, [book, form]);

  // Clear any inline edit modes and buffers when language/view resets
  useEffect(() => {
    setIsEditingTranslatedTitle(false);
    setIsEditingTranslatedSeries(false);
    setIsEditingTranslatedBlurb(false);
    setEditedTranslatedTitle('');
    setEditedTranslatedSeries('');
    setEditedTranslatedBlurb('');
  }, [selectedLanguage, resetCounter]);

  // Persist demo translated metadata per book in localStorage
  const storageKey = useMemo(() => (bookguid ? `demoTranslated:${bookguid}` : ''), [bookguid]);
  const neverStorageKey = useMemo(() => (bookguid ? `demoNeverTranslate:${bookguid}` : ''), [bookguid]);
  const alwaysStorageKey = useMemo(() => (bookguid ? `demoAlwaysTranslate:${bookguid}` : ''), [bookguid]);
  const chapterStoreKey = useMemo(() => (bookguid ? `demoTranslatedChapters:${bookguid}` : ''), [bookguid]);

  useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          setDemoTranslated(parsed);
        }
      }
    } catch (e) {
      console.warn('Demo: Failed to load persisted translations', e);
    }
  }, [storageKey]);

  useEffect(() => {
    if (!storageKey) return;
    try {
      if (demoTranslated && Object.keys(demoTranslated).length > 0) {
        localStorage.setItem(storageKey, JSON.stringify(demoTranslated));
      } else {
        localStorage.removeItem(storageKey);
      }
    } catch (e) {
      console.warn('Demo: Failed to persist translations', e);
    }
  }, [storageKey, demoTranslated]);

  // Load/save chapter translation flags
  useEffect(() => {
    if (!chapterStoreKey) return;
    try {
      const raw = localStorage.getItem(chapterStoreKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          setTranslatedChapters(parsed);
        }
      }
    } catch (e) {
      console.warn('Demo: Failed to load translated chapter flags', e);
    }
  }, [chapterStoreKey]);

  useEffect(() => {
    if (!chapterStoreKey) return;
    try {
      localStorage.setItem(chapterStoreKey, JSON.stringify(translatedChapters || {}));
    } catch (e) {
      console.warn('Demo: Failed to persist translated chapter flags', e);
    }
  }, [chapterStoreKey, translatedChapters]);

  // Demo: load default always/never lists from JSON, then overlay localStorage
  const loadRuleData = useCallback(async () => {
    if (!bookguid) return;
    try {
      const [neverRes, alwaysRes] = await Promise.all([
        fetch(`/demo/books/${bookguid}/never_translate.json`),
        fetch(`/demo/books/${bookguid}/always_translate.json`),
      ]);
      let neverDefaults: Record<string, string[]> = {};
      let alwaysDefaults: Record<string, string[]> = {};
      if (neverRes.ok) {
        neverDefaults = await neverRes.json();
      }
      if (alwaysRes.ok) {
        alwaysDefaults = await alwaysRes.json();
      }

      let neverOverride: Record<string, string[]> = {};
      let alwaysOverride: Record<string, string[]> = {};
      try {
        const rawN = neverStorageKey ? localStorage.getItem(neverStorageKey) : null;
        if (rawN) neverOverride = JSON.parse(rawN);
      } catch (e) {
        console.warn('Demo: Failed to load persisted never-translate', e);
      }
      try {
        const rawA = alwaysStorageKey ? localStorage.getItem(alwaysStorageKey) : null;
        if (rawA) alwaysOverride = JSON.parse(rawA);
      } catch (e) {
        console.warn('Demo: Failed to load persisted always-translate', e);
      }

      setNeverWords({ ...neverDefaults, ...neverOverride });
      setAlwaysWords({ ...alwaysDefaults, ...alwaysOverride });
    } catch (e) {
      console.warn('Demo: Failed to load translate rules', e);
      setNeverWords({});
      setAlwaysWords({});
    }
  }, [bookguid, neverStorageKey, alwaysStorageKey]);

  useEffect(() => {
    loadRuleData();
  }, [loadRuleData, resetCounter]);

  // Persist demo always/never lists
  useEffect(() => {
    if (!neverStorageKey) return;
    try {
      if (neverWords && Object.keys(neverWords).length > 0) {
        localStorage.setItem(neverStorageKey, JSON.stringify(neverWords));
      } else {
        localStorage.removeItem(neverStorageKey);
      }
    } catch (e) {
      console.warn('Demo: Failed to persist never-translate', e);
    }
  }, [neverStorageKey, neverWords]);

  useEffect(() => {
    if (!alwaysStorageKey) return;
    try {
      if (alwaysWords && Object.keys(alwaysWords).length > 0) {
        localStorage.setItem(alwaysStorageKey, JSON.stringify(alwaysWords));
      } else {
        localStorage.removeItem(alwaysStorageKey);
      }
    } catch (e) {
      console.warn('Demo: Failed to persist always-translate', e);
    }
  }, [alwaysStorageKey, alwaysWords]);

  const applyFormToState = (_changed: any, values: any) => {
    setBook((prev) => {
      if (!prev) return prev;
      const genresArr: string[] = Array.isArray(values.genres) ? values.genres.slice(0, 1) : [];
      return {
        ...prev,
        title: values.title,
        author: values.author,
        series: values.series,
        booknumber: values.booknumber,
        language: values.language,
        blurb: values.blurb,
        genres: genresArr,
        tropes: values.tropes || [],
        keywords: values.keywords || [],
      };
    });
  };

  const onSaveDemo = () => {
    message.success('Metadata saved');
  };

  // Demo translation scope + pending cost
  const [translateScope, setTranslateScope] = useState<'chapter' | 'book' | null>(null);
  const [pendingTranslateWords, setPendingTranslateWords] = useState<number>(0);

  const computeChapterWordCount = useCallback((order: number): number => {
    const ch = chapters.find(c => c.order === order);
    return (typeof ch?.word_count === 'number' ? ch!.word_count! : 0) || 0;
  }, [chapters]);

  const getTargetLanguage = useCallback((): string => {
    if (!selectedLanguage || selectedLanguage === 'original') return '';
    return getTargetLanguageFromPair(selectedLanguage) || '';
  }, [selectedLanguage]);

  const onTranslateBook = () => {
    // Open modal without requiring a pre-selected page language
    setTranslateScope('book');
    setPendingTranslateWords(0);
    setIsTranslateModalVisible(true);
  };
  
  const onExportBook = () => {
    setIsExportModalVisible(true);
  };
  
  const onResetDemo = () => {
    setHumanReviewModalVisible(false);
    setIsTranslateModalVisible(false);
    setIsExportModalVisible(false);
    setIsMetaModalVisible(false);
    setIsTitleOptionsVisible(false);
    setIsKeywordModalVisible(false);
    setIsSuggestingKeywords(false);
    setIsNeverModalVisible(false);
    setIsAlwaysModalVisible(false);
    setCurrentLanguagePair('');
    setSuggestedKeywordsReady({});
    setIsTranslating(false);
    setTranslateMeta({ title: false, series: false, blurb: false });
    setTargetLangMeta('');
    setQuotationStyle('default');
    setDemoTranslated({});
    setTranslatedChapters({});
    setNeverWords({});
    setAlwaysWords({});
    try {
      const key = storageKey || (bookguid ? `demoTranslated:${bookguid}` : '');
      if (key) localStorage.removeItem(key);
      const nk = neverStorageKey || (bookguid ? `demoNeverTranslate:${bookguid}` : '');
      if (nk) localStorage.removeItem(nk);
      const ak = alwaysStorageKey || (bookguid ? `demoAlwaysTranslate:${bookguid}` : '');
      if (ak) localStorage.removeItem(ak);
      const ck = chapterStoreKey || (bookguid ? `demoTranslatedChapters:${bookguid}` : '');
      if (ck) localStorage.removeItem(ck);
    } catch (e) {
      // ignore
    }
    setSelectedLanguage('original');
    setActiveTab('metadata');
    setShowMetadataPanel(true);
    setSelectedChapterGuid(null);
    form.resetFields();
    loadData();
    setResetCounter((c) => c + 1);
    message.success('Demo reset to default state.');
  };

  const isTranslatedView = selectedLanguage !== 'original';
  const isChapterView = selectedChapterGuid !== null && !showMetadataPanel;

  // Helper to mark a chapter as translated for current target language
  const markCurrentChapterTranslated = useCallback(() => {
    if (!isChapterView || !selectedChapter) return;
    const target = isTranslatedView ? (getTargetLanguageFromPair(selectedLanguage) || '').trim() : '';
    if (!target) {
      message.warning('Select a target language first.');
      return;
    }
    const order = selectedChapter.order;
    setTranslatedChapters((prev) => {
      const list = new Set([...(prev[target] || [])]);
      list.add(order);
      return { ...prev, [target]: Array.from(list).sort((a, b) => a - b) };
    });
  }, [isChapterView, isTranslatedView, selectedChapter, selectedLanguage]);

  const deleteMenuItems = [
    { key: 'translated', label: 'Delete Translated Chapters', disabled: true },
    { key: 'proofread', label: 'Delete Proofread Chapters', disabled: true },
  ];

  const renderOriginalMetadata = () => (
    <Form
      data-testid="tutorial:metadata:original-form"
      form={form}
      layout="vertical"
      onValuesChange={applyFormToState}
      style={{ marginTop: 0, marginBottom: 0 }}
    >
      <FormGrid3>
        <Form.Item
          label={<strong>Title</strong>}
          name="title"
          rules={[{ required: true, message: 'Title is required' }]}
          style={{ marginBottom: 10 }}
          labelCol={{ style: { marginBottom: 0, lineHeight: '1' } }}
        >
          <Input placeholder="Enter book title" />
        </Form.Item>

        <Form.Item
          label={<strong>Author</strong>}
          name="author"
          style={{ marginBottom: 10 }}
          labelCol={{ style: { marginBottom: 0, lineHeight: '1' } }}
        >
          <Input placeholder="Enter author name" />
        </Form.Item>

        <Form.Item
          label={<strong>Series</strong>}
          name="series"
          style={{ marginBottom: 10 }}
          labelCol={{ style: { marginBottom: 0, lineHeight: '1' } }}
        >
          <Input placeholder="Enter series name" />
        </Form.Item>
      </FormGrid3>

      <FormGrid3>
        <Form.Item
          label={<strong>Series Book Number</strong>}
          name="booknumber"
          style={{ marginBottom: 10 }}
          labelCol={{ style: { marginBottom: 0, lineHeight: '1' } }}
        >
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          label={<strong>Language</strong>}
          name="language"
          style={{ marginBottom: 10 }}
          labelCol={{ style: { marginBottom: 0, lineHeight: '1' } }}
        >
          <Input placeholder="e.g., English" />
        </Form.Item>

        <Form.Item
          data-testid="tutorial:metadata:keywords"
          label={
                <Space size={4}>
              <strong>Primary Genre</strong>
              <Tooltip title="Select or type the primary genre of your book. The demo enforces a single primary genre.">
                <QuestionCircleOutlined />
              </Tooltip>
            </Space>
          }
          name="genres"
          style={{ marginBottom: 10 }}
          labelCol={{ style: { marginBottom: 0, lineHeight: '1' } }}
        >
          <Select
            mode="tags"
            style={{ width: '100%' }}
            placeholder="Type or select a primary genre"
            onChange={(vals: string[]) => {
              if (vals.length > 1) {
                message.error('You can only select one primary genre');
                const first = vals.slice(0, 1);
                form.setFieldsValue({ genres: first });
                setBook((prev) => (prev ? { ...prev, genres: first } : prev));
              }
            }}
          >
            {(book?.genres || []).map((g) => (
              <Select.Option key={g} value={g}>
                {g}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </FormGrid3>

      <FormGrid2>
        <Form.Item
          label={
              <Space size={4}>
              <strong>Tropes</strong>
              <Tooltip title="Enter one or more tropes related to the book.">
                <QuestionCircleOutlined />
              </Tooltip>
            </Space>
          }
          name="tropes"
          style={{ marginBottom: 10 }}
          labelCol={{ style: { marginBottom: 0, lineHeight: '1' } }}
        >
          <Select
            mode="tags"
            style={{ width: '100%' }}
            placeholder="Type or select tropes"
          />
        </Form.Item>

        <Form.Item
          label={
            <Space>
              <strong>Keywords</strong>
              <Tooltip title="Enter one or more keywords related to the book (e.g., Amazon keywords).">
                <QuestionCircleOutlined />
              </Tooltip>
            </Space>
          }
          name="keywords"
          style={{ marginBottom: 10 }}
          labelCol={{ style: { marginBottom: 0, lineHeight: '1' } }}
        >
          <Select
            mode="tags"
            style={{ width: '100%' }}
            placeholder="Type or select keywords"
          />
        </Form.Item>
      </FormGrid2>

      <Form.Item
        data-testid="tutorial:metadata:blurb"
        label={
          <Space>
            <strong>Blurb</strong>
            <Tooltip title="Generate Blurb">
              <Button
                type="text"
                size="small"
                aria-label="Generate Blurb"
                onClick={() => message.info('Generate Blurb coming soon (demo placeholder).')}
                icon={<BulbOutlined />}
              />
            </Tooltip>
            <Tooltip title="Export blurb to TXT (disabled in demo)">
              <Button
                type="text"
                size="small"
                aria-label="Export Blurb to TXT"
                icon={<ExportOutlined />}
                disabled
              />
            </Tooltip>
          </Space>
        }
        name="blurb"
        style={{ marginBottom: 10 }}
        labelCol={{ style: { marginBottom: 0, lineHeight: '1' } }}
        valuePropName="value"
      >
        <ReactQuill
          onChange={(value) => {
            form.setFieldsValue({ blurb: value });
            setBook((prev) => (prev ? { ...prev, blurb: value } : prev));
          }}
          modules={{
            toolbar: [
              ['bold', 'italic', 'underline'],
              ['clean'],
            ],
          }}
          placeholder="Enter book blurb"
        />
      </Form.Item>
    </Form>
  );

  const renderTranslatedMetadata = () => {
    // Parse "English-German" -> ["English","German"]
    const getLangPair = (): { source: string; target: string } => {
      if (!selectedLanguage || selectedLanguage === 'original') {
        return { source: book?.language || 'English', target: '' };
      }
      const { sourceLang, targetLang } = splitLanguagePair(selectedLanguage);
      return {
        source: (sourceLang || book?.language || 'English').trim(),
        target: (targetLang || '').trim(),
      };
    };

    const { source: sourceLang, target: targetLang } = getLangPair();

    // Use stored demo translated values if present
    const stored = (targetLang && demoTranslated[targetLang]) || {};
    const translatedTitle = stored?.title || '';
    const translatedSeries = stored?.series || '';
    const translatedBlurb = stored?.blurb || '';

    // Demo keywords table builder (Translated | English meaning) for German only in demo
    type KWRow = { translated: string; english: string };

    const DEMO_GERMAN_KW_MAP: Record<string, string> = {
      'time travel': 'Zeitreise',
      'science fiction': 'Science-Fiction',
      'sci-fi': 'Science-Fiction',
      'dystopia': 'Dystopie',
      'utopia': 'Utopie',
      'future': 'Zukunft',
      'future society': 'Zukunftsgesellschaft',
      'victorian era': 'Viktorianische Ã„ra',
      'romance': 'Liebesroman',
      'mystery': 'Mystery',
      'thriller': 'Thriller',
      'fantasy': 'Fantasy',
      'horror': 'Horror',
      'historical fiction': 'Historischer Roman',
      'adventure': 'Abenteuer',
      'detective': 'Detektiv',
      'space opera': 'Space Opera',
      'alien': 'AuÃŸerirdischer',
      'aliens': 'AuÃŸerirdische',
      'robot': 'Roboter',
      'robots': 'Roboter',
      'artificial intelligence': 'KÃ¼nstliche Intelligenz',
      'ai': 'KI',
      'love': 'Liebe',
      'war': 'Krieg',
      'politics': 'Politik',
      'geometry': 'Geometrie',
      'dimensions': 'Dimensionen',
      'dimension': 'Dimension',
      'mathematics': 'Mathematik',
      'satire': 'Satire',
    };

    const buildKeywordsGerman = (): KWRow[] => {
      if (targetLang !== 'German') return [];
      const title = (book?.title || '').trim();

      // Curated keywords for known demo books
      if (/^the time machine$/i.test(title)) {
        return [
          { translated: 'Zeitreise', english: 'time travel' },
          { translated: 'Science-Fiction', english: 'science fiction' },
          { translated: 'Dystopie', english: 'dystopia' },
          { translated: 'Zukunftsgesellschaft', english: 'future society' },
          { translated: 'Viktorianische Ã„ra', english: 'Victorian era' },
          { translated: 'Sozialdarwinismus', english: 'social darwinism' },
          { translated: 'Postapokalyptik', english: 'post-apocalyptic' },
        ];
      }
      if (/flatland/i.test(title)) {
        return [
          { translated: 'Geometrie', english: 'geometry' },
          { translated: 'Dimensionen', english: 'dimensions' },
          { translated: 'Satire', english: 'satire' },
          { translated: 'Mathematik', english: 'mathematics' },
          { translated: 'Gesellschaftskritik', english: 'social criticism' },
          { translated: 'Allegorie', english: 'allegory' },
          { translated: 'Zweidimensional', english: 'two-dimensional' },
        ];
      }

      // Otherwise map any existing keywords with a small dictionary; fallback to original
      const kws = book?.keywords || [];
      return kws.map((k) => {
        const key = (k || '').toString().trim();
        const lower = key.toLowerCase();
        const translated = DEMO_GERMAN_KW_MAP[lower] || key;
        return { translated, english: key };
      });
    };

    const buildKeywordsFrench = (): KWRow[] => {
      if (targetLang !== 'French') return [];
      const title = (book?.title || '').trim();
      if (/^the time machine$/i.test(title)) {
        return [
          { translated: 'Voyage dans le temps', english: 'time travel' },
          { translated: 'Science-fiction', english: 'science fiction' },
          { translated: 'Dystopie', english: 'dystopia' },
          { translated: 'SociÃ©tÃ© future', english: 'future society' },
          { translated: 'Ã‰poque victorienne', english: 'Victorian era' },
          { translated: 'Darwinisme social', english: 'social darwinism' },
          { translated: 'Post-apocalyptique', english: 'post-apocalyptic' },
        ];
      }
      if (/flatland/i.test(title)) {
        return [
          { translated: 'GÃ©omÃ©trie', english: 'geometry' },
          { translated: 'Dimensions', english: 'dimensions' },
          { translated: 'Satire', english: 'satire' },
          { translated: 'MathÃ©matiques', english: 'mathematics' },
          { translated: 'Critique sociale', english: 'social criticism' },
          { translated: 'AllÃ©gorie', english: 'allegory' },
          { translated: 'Bidimensionnel', english: 'two-dimensional' },
        ];
      }
      const DEMO_FRENCH_KW_MAP: Record<string, string> = {
        'time travel': 'voyage dans le temps',
        'science fiction': 'science-fiction',
        'sci-fi': 'science-fiction',
        'dystopia': 'dystopie',
        'utopia': 'utopie',
        'future': 'avenir',
        'future society': 'sociÃ©tÃ© future',
        'victorian era': 'Ã©poque victorienne',
        'romance': 'romance',
        'mystery': 'mystÃ¨re',
        'thriller': 'thriller',
        'fantasy': 'fantasy',
        'horror': 'horreur',
        'historical fiction': 'roman historique',
        'adventure': 'aventure',
        'detective': 'dÃ©tective',
        'space opera': 'space opera',
        'alien': 'extraterrestre',
        'aliens': 'extraterrestres',
        'robot': 'robot',
        'robots': 'robots',
        'artificial intelligence': 'intelligence artificielle',
        'ai': 'IA',
        'love': 'amour',
        'war': 'guerre',
        'politics': 'politique',
        'geometry': 'gÃ©omÃ©trie',
        'dimensions': 'dimensions',
        'dimension': 'dimension',
        'mathematics': 'mathÃ©matiques',
        'satire': 'satire',
      };
      const kws = book?.keywords || [];
      return kws.map((k) => {
        const key = (k || '').toString().trim();
        const lower = key.toLowerCase();
        const translated = DEMO_FRENCH_KW_MAP[lower] || key;
        return { translated, english: key };
      });
    };

    const keywordRows: KWRow[] =
      targetLang === 'German' ? buildKeywordsGerman() :
      targetLang === 'French' ? buildKeywordsFrench() :
      [];

    const isKWReady = !!suggestedKeywordsReady[targetLang];

    return (
      <div data-testid="tutorial:metadata:translated-workspace">
        <Form data-testid="tutorial:metadata:translated-form" layout="vertical" style={{ marginTop: 0, marginBottom: 0 }}>
          <FormGrid2>
            <Form.Item
              label={
                <Space size={4}>
                  <strong>Translated Title:</strong>
                  <Tooltip title={isEditingTranslatedTitle ? 'Editing enabled' : 'Enable editing'}>
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => {
                        if (!isEditingTranslatedTitle) {
                          setEditedTranslatedTitle(translatedTitle || '');
                          setIsEditingTranslatedTitle(true);
                        }
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Save translated title">
                    <Button
                      type="text"
                      size="small"
                      icon={<SaveOutlined />}
                      disabled={!isEditingTranslatedTitle}
                      onClick={() => {
                        if (!targetLang) return;
                        const value = editedTranslatedTitle.trim();
                        setDemoTranslated((prev) => ({
                          ...prev,
                          [targetLang]: {
                            ...(prev[targetLang] || {}),
                            title: value,
                          },
                        }));
                        setIsEditingTranslatedTitle(false);
                        message.success('Demo: Translated title saved.');
                      }}
                    />
                  </Tooltip>
                </Space>
              }
              style={{ marginBottom: 10 }}
              labelCol={{ style: { marginBottom: 0, lineHeight: '1' } }}
            >
              {isEditingTranslatedTitle ? (
                <Input
                  value={editedTranslatedTitle}
                  onChange={(e) => setEditedTranslatedTitle(e.target.value)}
                  placeholder="<Not translated>"
                />
              ) : (
                <Input value={translatedTitle} disabled placeholder="<Not translated>" />
              )}
            </Form.Item>

            <Form.Item
              label={
                <Space size={4}>
                  <strong>Translated Series Name:</strong>
                  <Tooltip title={isEditingTranslatedSeries ? 'Editing enabled' : 'Enable editing'}>
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => {
                        if (!isEditingTranslatedSeries) {
                          setEditedTranslatedSeries(translatedSeries || '');
                          setIsEditingTranslatedSeries(true);
                        }
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Save translated series name">
                    <Button
                      type="text"
                      size="small"
                      icon={<SaveOutlined />}
                      disabled={!isEditingTranslatedSeries}
                      onClick={() => {
                        if (!targetLang) return;
                        const value = editedTranslatedSeries.trim();
                        setDemoTranslated((prev) => ({
                          ...prev,
                          [targetLang]: {
                            ...(prev[targetLang] || {}),
                            series: value,
                          },
                        }));
                        setIsEditingTranslatedSeries(false);
                        message.success('Demo: Translated series name saved.');
                      }}
                    />
                  </Tooltip>
                </Space>
              }
              style={{ marginBottom: 10 }}
              labelCol={{ style: { marginBottom: 0, lineHeight: '1' } }}
            >
              {isEditingTranslatedSeries ? (
                <Input
                  value={editedTranslatedSeries}
                  onChange={(e) => setEditedTranslatedSeries(e.target.value)}
                  placeholder="<Not translated>"
                />
              ) : (
                <Input value={translatedSeries} disabled placeholder="<Not translated>" />
              )}
            </Form.Item>
          </FormGrid2>

          <Form.Item
            label={
              <Space size={4}>
                <strong>Translated Blurb:</strong>
                <Tooltip title={isEditingTranslatedBlurb ? 'Editing enabled' : 'Enable editing'}>
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => {
                      if (!isEditingTranslatedBlurb) {
                        setEditedTranslatedBlurb(translatedBlurb || '');
                        setIsEditingTranslatedBlurb(true);
                      }
                    }}
                  />
                </Tooltip>
                <Tooltip title="Save translated blurb">
                  <Button
                    type="text"
                    size="small"
                    icon={<SaveOutlined />}
                    disabled={!isEditingTranslatedBlurb}
                    onClick={() => {
                      if (!targetLang) return;
                      setDemoTranslated((prev) => ({
                        ...prev,
                        [targetLang]: {
                          ...(prev[targetLang] || {}),
                          blurb: editedTranslatedBlurb,
                        },
                      }));
                      setIsEditingTranslatedBlurb(false);
                      message.success('Demo: Translated blurb saved.');
                    }}
                  />
                </Tooltip>
              </Space>
            }
            style={{ marginBottom: 10 }}
            labelCol={{ style: { marginBottom: 0, lineHeight: '1' } }}
          >
            {isEditingTranslatedBlurb ? (
              <ReactQuill
                value={sanitizeRichTextHtml(editedTranslatedBlurb)}
                onChange={(value) => setEditedTranslatedBlurb(value)}
                modules={{
                  toolbar: [
                    ['bold', 'italic', 'underline'],
                    ['clean'],
                  ],
                }}
                placeholder="Enter translated blurb"
              />
            ) : translatedBlurb ? (
              <div
                role="textbox"
                aria-readonly
                aria-disabled
                tabIndex={-1}
                onClick={(e) => {
                  const anchor = (e.target as HTMLElement).closest('a');
                  if (anchor) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: 4,
                  padding: 12,
                  background: '#f5f5f5',
                  color: 'rgba(0,0,0,0.65)',
                  minHeight: '150px',
                  overflowX: 'auto',
                  overflowY: 'auto',
                  cursor: 'not-allowed',
                  userSelect: 'text',
                  marginBottom: '10px',
                }}
                dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(translatedBlurb) }}
              />
            ) : (
              <div
                role="textbox"
                aria-readonly
                aria-disabled
                tabIndex={-1}
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: 4,
                  padding: 12,
                  background: '#f5f5f5',
                  color: 'rgba(0,0,0,0.45)',
                  minHeight: '150px',
                  cursor: 'not-allowed',
                  userSelect: 'text',
                  marginBottom: '10px',
                  fontStyle: 'italic',
                }}
              >
                {'<Not translated>'}
              </div>
            )}
          </Form.Item>
        </Form>

        {/* Never/Always Translate sections (Demo) */}
        <div data-testid="tutorial:translation-rules:workspace" style={{ display: 'flex', gap: 24, alignItems: 'flex-start', marginTop: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <div data-testid="tutorial:translation-rules:never" style={{ flex: 1, minWidth: 260 }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <strong>Never Translate</strong>
              <Tooltip title="The AI will never translate these words or phrases">
                <QuestionCircleOutlined style={{ marginLeft: 8, cursor: 'pointer', color: '#1677ff' }} />
              </Tooltip>
              <Tooltip title="Edit Never Translate Text">
                <EditOutlined
                  onClick={() => {
                    setCurrentLanguagePair(`${sourceLang}-${targetLang}`);
                    setIsNeverModalVisible(true);
                  }}
                  style={{ cursor: 'pointer', marginLeft: 10 }}
                />
              </Tooltip>
            </div>
            <div>
              {(neverWords[`${sourceLang}-${targetLang}`] || []).map((word, index) => (
                <Tag key={index}>{word}</Tag>
              ))}
            </div>
          </div>
          <div data-testid="tutorial:translation-rules:always" style={{ flex: 1, minWidth: 260 }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <strong>Always Translate</strong>
              <Tooltip title="The AI will always translate the source text to the specified target text">
                <QuestionCircleOutlined style={{ marginLeft: 8, cursor: 'pointer', color: '#1677ff' }} />
              </Tooltip>
              <Tooltip title="Edit Always Translate Text">
                <EditOutlined
                  onClick={() => {
                    setCurrentLanguagePair(`${sourceLang}-${targetLang}`);
                    setIsAlwaysModalVisible(true);
                  }}
                  style={{ cursor: 'pointer', marginLeft: 10 }}
                />
              </Tooltip>
            </div>
            <div>
              {(alwaysWords[`${sourceLang}-${targetLang}`] || []).map((pair, index) => (
                <Tag key={index}>{pair.includes('|') ? pair.replace('|', ' -> ') : pair}</Tag>
              ))}
            </div>
          </div>
        </div>

        <Divider />

        <div data-testid="tutorial:metadata:translated-keywords" style={{ maxHeight: 180, overflowY: 'auto', overflowX: 'auto', marginBottom: 0, maxWidth: 720 }}>
          <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', borderSpacing: 0, lineHeight: 1.1, fontSize: 13 }}>
            <colgroup>
              <col style={{ width: '35%' }} />
              <col style={{ width: '75%' }} />
            </colgroup>
            <thead>
              <tr>
                <th style={{ borderBottom: '1px solid #ddd', textAlign: 'left', wordBreak: 'break-word', padding: '4px 0' }}>
                  {`Keyword in ${targetLang || 'Target'}`}
                </th>
                <th style={{ borderBottom: '1px solid #ddd', textAlign: 'left', wordBreak: 'break-word', padding: '4px 0' }}>
                  {`Meaning in ${sourceLang || 'Source'}`}
                </th>
              </tr>
            </thead>
            <tbody>
              {!isKWReady ? (
                <tr>
                  <td colSpan={2} style={{ padding: '2px 0', color: '#8c8c8c', fontStyle: 'italic' }}>
                    Keywords will appear after you generate suggestions.
                  </td>
                </tr>
              ) : keywordRows.length === 0 ? (
                <tr>
                  <td colSpan={2} style={{ padding: '2px 0', color: '#8c8c8c', fontStyle: 'italic' }}>
                    No keywords found.
                  </td>
                </tr>
              ) : (
                keywordRows.map((row, idx) => (
                  <tr key={idx}>
                    <td style={{ padding: '2px 0', wordBreak: 'break-word' }}>
                      <strong>{row.translated}</strong>
                    </td>
                    <td style={{ padding: '2px 0', wordBreak: 'break-word' }}>{row.english}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Build title options based on book and language
  const getTitleOptionsFor = (targetLang: string): TranslationOption[] => {
    const t = (book?.title || '').trim();
    if (/^the time machine$/i.test(t)) {
      if (targetLang === 'German') {
        return [
          { translatedText: 'Die Zeitmaschine', sourceTrans: 'The Time Machine', desc: 'Faithful and instantly recognizable; keeps the iconic, sci-fi clarity.' },
          { translatedText: 'Reise durch die Zeit', sourceTrans: 'Journey Through Time', desc: 'Emphasizes adventure and movement, broadening mainstream appeal.' },
          { translatedText: 'Jenseits der Jahrtausende', sourceTrans: 'Beyond the Millennia', desc: 'Hints at the awe and distance of the far-future setting.' },
        ];
      }
      if (targetLang === 'French') {
        return [
          { translatedText: 'La Machine Ã  explorer le temps', sourceTrans: 'The Machine for Exploring Time', desc: 'Precise, canonical phrasing with a clean scientific ring.' },
          { translatedText: 'Voyage Ã  travers le temps', sourceTrans: 'Journey Through Time', desc: 'Foregrounds the odyssey aspect for a wide audience.' },
          { translatedText: 'Au-delÃ  des millÃ©naires', sourceTrans: 'Beyond the Millennia', desc: 'Evokes scale and mystery while staying elegant.' },
        ];
      }
    }
    if (/flatland/i.test(t)) {
      if (targetLang === 'German') {
        return [
          { translatedText: 'FlÃ¤chenland', sourceTrans: 'Flatland / Land of Surfaces', desc: 'Established, punchy, and preserves the originalâ€™s wordplay.' },
          { translatedText: 'Das Land der zwei Dimensionen', sourceTrans: 'The Land of Two Dimensions', desc: 'Spells out the premise for younger or general readers.' },
          { translatedText: 'Aufstieg in die dritte Dimension', sourceTrans: 'Ascent into the Third Dimension', desc: 'Teases the pivotal revelation and sense of discovery.' },
        ];
      }
      if (targetLang === 'French') {
        return [
          { translatedText: 'Flatland : Un roman Ã  plusieurs dimensions', sourceTrans: 'Flatland: A Novel of Several Dimensions', desc: 'Faithful to the subtitle with modern, natural phrasing.' },
          { translatedText: 'Le Pays plat', sourceTrans: 'The Flat Country/Land', desc: 'Simple and memorable, keeping the allegorical flavor.' },
          { translatedText: 'Vers la troisiÃ¨me dimension', sourceTrans: 'Toward the Third Dimension', desc: 'Highlights the transformational leap that drives the plot.' },
        ];
      }
    }
    return [];
  };

  const handleStartMetadataTranslate = () => {
    if (!targetLangMeta || (!translateMeta.title && !translateMeta.blurb)) {
      message.error('Select target language and at least one field.');
      return;
    }
    setIsMetaModalVisible(false);
    setIsTranslating(true);
    setTimeout(() => {
      setIsTranslating(false);
      // Apply blurb if requested
      if (translateMeta.blurb) {
        const blurbMap: Record<string, string | undefined> = {
          German: book?.blurb_de,
          French: book?.blurb_fr,
        };
        const blurbHtml = blurbMap[targetLangMeta] || '<em>[Demo translated blurb]</em>';
        setDemoTranslated((prev) => ({
          ...prev,
          [targetLangMeta]: {
            ...(prev[targetLangMeta] || {}),
            blurb: blurbHtml,
          },
        }));
      }
      // If title requested, open options
      if (translateMeta.title) {
        const options = getTitleOptionsFor(targetLangMeta);
        setTitleOptions(options);
        setIsTitleOptionsVisible(true);
      } else {
        message.success('Demo: Metadata translation complete.');
      }
    }, 3000);
  };

  const handleSaveTitleOption = (opt: TranslationOption) => {
    setDemoTranslated((prev) => ({
      ...prev,
      [targetLangMeta]: {
        ...(prev[targetLangMeta] || {}),
        title: opt.translatedText,
      },
    }));
    setIsTitleOptionsVisible(false);
    message.success('Demo: Title translation saved.');
  };

  // Listen for global demo reset from the header
  useEffect(() => {
    const handler = () => onResetDemo();
    // Type cast to satisfy TS for custom event without payload
    window.addEventListener('demo:reset', handler as unknown as EventListener);
    return () => {
      window.removeEventListener('demo:reset', handler as unknown as EventListener);
    };
  }, [onResetDemo]);

  return (
    <>
      <DemoHeader />
      <PageContainer>
        <MainWrap>
          <TopBar data-testid="tutorial:book-detail:header">
            <div style={{ width: 120 }} />
            <TitleCenter>
              <Space align="center" size={8}>
                <Title level={4} style={{ margin: 0 }}>{book?.title || ''}</Title>
                <Tooltip title={showMetadataPanel && activeTab === 'metadata' ? 'Already viewing metadata' : 'Open metadata'}>
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    disabled={showMetadataPanel && activeTab === 'metadata'}
                    onClick={() => {
                      setShowMetadataPanel(true);
                      setActiveTab('metadata');
                    }}
                  />
                </Tooltip>
              </Space>
            </TitleCenter>
            <div style={{ width: 120 }} />
          </TopBar>
          {loading ? (
            <div className="loading-card-wrapper" style={{ height: '60vh' }}>
              <LoadingCard label="Loading demo book..." />
            </div>
          ) : (
            <ContentWrap>
              {/* Sidebar */}
              <Sidebar data-testid="tutorial:book-detail:chapter-rail">
                <SidebarHeader>Chapter List</SidebarHeader>
                <ChaptersScroll>
                  {orderedChapters.map((chapter, index) => {
                    const active = chapter.chapter_guid === selectedChapterGuid;
                    return (
                      <ChapterItem
                        key={chapter.chapter_guid}
                        data-testid={index === 0 ? 'tutorial:book-detail:first-chapter' : undefined}
                        $active={active}
                        onClick={() => { setSelectedChapterGuid(chapter.chapter_guid); setShowMetadataPanel(false); }}
                      >
                        <ChapterName>{chapter.chapter_name}</ChapterName>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <Tooltip title="View/Edit Metadata (disabled in demo)">
                            <span>
                              <Button
                                size="small"
                                type="text"
                                icon={<EditOutlined />}
                                disabled
                                aria-disabled
                              />
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete chapter (disabled in demo)">
                            <span>
                              <Button size="small" type="text" icon={<DeleteOutlined />} disabled aria-disabled />
                            </span>
                          </Tooltip>
                        </div>
                      </ChapterItem>
                    );
                  })}
                </ChaptersScroll>
              </Sidebar>

              {/* Right Panel */}
              <MainPanel data-testid="tutorial:book-detail:workspace">
                <TabCard title={null}>
                  <MetaContent>
                    <ScrollArea>
                      <TwoCol style={{ gridTemplateColumns: isChapterView ? '1fr' : undefined }}>
                        {/* Left: Cover + actions */}
                        <LeftCol style={{ display: isChapterView ? 'none' : undefined }}>
                          {book?.coverUrl ? (
                            <CoverImg src={book.coverUrl} alt="Cover" />
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 260, width: 180, justifyContent: 'center', border: '1px solid #f0f0f0', borderRadius: 6 }}>
                              <FileImageOutlined style={{ fontSize: 40, color: '#d9d9d9' }} />
                            </div>
                          )}

                          {/* Cover actions (disabled in demo) */}
                          <CoverButtons data-testid="tutorial:metadata:covers">
                            <Tooltip title="Translate Cover (disabled in demo)">
                              <span>
                                <Button type="default" size="middle" icon={<TranslationOutlined />} disabled aria-disabled style={{ width: '100%' }}>
                                  Translate Cover
                                </Button>
                              </span>
                            </Tooltip>
                            <Tooltip title="Cover History (disabled in demo)">
                              <span>
                                <Button type="default" size="middle" icon={<HistoryOutlined />} disabled aria-disabled style={{ width: '100%' }}>
                                  Cover History
                                </Button>
                              </span>
                            </Tooltip>
                          </CoverButtons>

                          <ButtonsStack data-testid="tutorial:book-detail:book-actions">
                            <Tooltip title={'Translate Book'}>
                              <Button
                                data-testid="tutorial:translation:open-book"
                                type="default"
                                size="middle"
                                icon={<TranslationOutlined />}
                                onClick={onTranslateBook}
                                style={{ width: '100%' }}
                              />
                            </Tooltip>
                            <Tooltip title="Proofread Book is disabled in the demo">
                              <span>
                                <Button data-testid="tutorial:proofread:open-book" type="default" size="middle" icon={<CheckOutlined />} disabled aria-disabled style={{ width: '100%' }} />
                              </span>
                            </Tooltip>
                            <Tooltip title="Export Book">
                              <Button data-testid="tutorial:export:open-book" type="default" size="middle" icon={<DownloadOutlined />} onClick={onExportBook} style={{ width: '100%' }} />
                            </Tooltip>
                          </ButtonsStack>
                        </LeftCol>

                        {/* Right: Language header + Tabs */}
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <LanguageHeader data-testid="tutorial:book-detail:language-workspaces">
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <div className="label">
                                <GlobalOutlined style={{ marginRight: 8, color: '#1677ff' }} />
                                <span>Language:</span>
                              </div>
                              <Select
                                data-testid="tutorial:book-detail:language-selector"
                                value={selectedLanguage}
                                onChange={setSelectedLanguage}
                                style={{ width: 300 }}
                                suffixIcon={
                                  <span className="ant-select-suffix">
                                    <DownOutlined style={{ color: '#1677ff', pointerEvents: 'none' }} />
                                  </span>
                                }
                              >
                                <Select.Option value="original">Original</Select.Option>
                                {/* Demo-only translated options to preview layout */}
                                <Select.Option value="English-German">German</Select.Option>
                                <Select.Option value="English-French">French</Select.Option>
                              </Select>
                            </div>

                            {activeTab !== 'audiobook' && (
                              <ActionsRight data-testid="tutorial:book-detail:actions-toolbar">
                                {isChapterView ? (
                                  <>
                                    <Tooltip title="Translate Chapter">
                                      <Button
                                        data-testid="tutorial:translation:open-chapter"
                                        icon={<TranslationOutlined />}
                                        onClick={() => {
                                          // Open modal; user selects target language inside
                                          setTranslateScope('chapter');
                                          setPendingTranslateWords(0);
                                          setIsTranslateModalVisible(true);
                                        }}
                                      />
                                    </Tooltip>
                                    <Tooltip title="Proofread Chapter (disabled in demo)">
                                      <span>
                                        <Button data-testid="tutorial:proofread:open-chapter" icon={<CheckOutlined />} disabled aria-disabled />
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Export Book">
                                      <Button data-testid="tutorial:export:open-chapter" icon={<DownloadOutlined />} onClick={() => setIsExportModalVisible(true)} />
                                    </Tooltip>
                                    <Tooltip title="Open metadata">
                                      <Button
                                        data-testid="tutorial:book-detail:open-metadata"
                                        icon={<SettingOutlined />}
                                        onClick={() => {
                                          setShowMetadataPanel(true);
                                          setActiveTab('metadata');
                                        }}
                                      />
                                    </Tooltip>
                                  </>
                                ) : (
                                  <>
                                    {!isTranslatedView && activeTab === 'metadata' && (
                                      <Tooltip title="Save Metadata">
                                        <Button
                                          data-testid="tutorial:metadata:save"
                                          type="primary"
                                          onClick={onSaveDemo}
                                          icon={<SaveOutlined />}
                                        />
                                      </Tooltip>
                                    )}

                                    <Tooltip title="Translate Metadata">
                                      <Button
                                        data-testid="tutorial:metadata:translate"
                                        icon={<TranslationOutlined />}
                                        onClick={() => {
                                          setTranslateMeta({ title: false, series: false, blurb: false });
                                          setTargetLangMeta('');
                                          setQuotationStyle('default');
                                          setIsMetaModalVisible(true);
                                        }}
                                      />
                                    </Tooltip>

                                    <Tooltip title="Suggest Keywords">
                                      <Button data-testid="tutorial:metadata:keyword-suggestions" icon={<BulbOutlined />} onClick={() => setIsKeywordModalVisible(true)} />
                                    </Tooltip>

                                    {isTranslatedView && (
                                      <Tooltip title="Delete Translated/Proofread chapters (disabled in demo)">
                                        <span>
                                          <Dropdown menu={{ items: deleteMenuItems }} trigger={['click']} disabled>
                                            <Button icon={<DeleteOutlined />} />
                                          </Dropdown>
                                        </span>
                                      </Tooltip>
                                    )}
                                  </>
                                )}
                              </ActionsRight>
                            )}
                          </LanguageHeader>

                          {isChapterView ? (
                            <div>
                              {!selectedChapter ? (
                                <div style={{ color: '#8c8c8c' }}>Select a chapter from the left to view its content.</div>
                              ) : (
                                <>
                                  {loadingChapter ? (
                                    <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
                                      <Spin />
                                    </div>
                                  ) : (
                                    <div
                                      data-testid="tutorial:book-detail:chapter-content"
                                      onClick={(e) => {
                                        const anchor = (e.target as HTMLElement).closest('a');
                                        if (anchor) {
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }
                                      }}
                                      style={{
                                        padding: 0,
                                        minHeight: 0,
                                        overflow: 'visible',
                                        wordBreak: 'break-word'
                                      }}
                                      dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(chapterHtml) }}
                                    />
                                  )}
                                </>
                              )}
                            </div>
                          ) : (
                            <Tabs data-testid="tutorial:book-detail:tabs" activeKey={activeTab} onChange={setActiveTab} type="card" size="large">
                              <TabPane tab={<span data-testid="tutorial:book-detail:metadata-tab">Metadata</span>} key="metadata">
                                {isTranslatedView ? renderTranslatedMetadata() : renderOriginalMetadata()}
                              </TabPane>
                              <TabPane tab={<span data-testid="tutorial:book-detail:audiobook-tab">Audiobook</span>} key="audiobook">
                                <div data-testid="tutorial:audiobook:workspace" style={{ color: '#8c8c8c' }}>Unavailable in Demo</div>
                              </TabPane>
                            </Tabs>
                          )}
                        </div>
                      </TwoCol>
                    </ScrollArea>
                  </MetaContent>

                </TabCard>
              </MainPanel>
            </ContentWrap>
          )}
        </MainWrap>
      </PageContainer>

      {/* Spinner modal for demo translation */}
      <Modal visible={isTranslating} footer={null} closable={false} centered>
        <div className="loading-card-wrapper" style={{ padding: 20 }}>
          <LoadingCard label="Translating..." />
        </div>
      </Modal>

      {/* Spinner modal for demo keyword suggestions */}
      <Modal visible={isSuggestingKeywords} footer={null} closable={false} centered>
        <div className="loading-card-wrapper" style={{ padding: 20 }}>
          <LoadingCard label="Generating keywords..." />
        </div>
      </Modal>

      {/* Demo: Metadata Translation modal */}
      <DemoMetadataTranslationModal
        visible={isMetaModalVisible}
        onCancel={() => setIsMetaModalVisible(false)}
        onTranslate={handleStartMetadataTranslate}
        bookLanguage={book?.language || 'English'}
        targetLanguage={targetLangMeta}
        setTargetLanguage={setTargetLangMeta}
        translateMetadata={translateMeta}
        setTranslateMetadata={setTranslateMeta}
        languages={['German', 'French']}
        quotationStyle={quotationStyle}
        setQuotationStyle={setQuotationStyle}
        resetCounter={resetCounter}
      />

      {/* Demo: Title options modal */}
      <DemoTranslationOptionsModal
        visible={isTitleOptionsVisible}
        onCancel={() => setIsTitleOptionsVisible(false)}
        onSave={handleSaveTitleOption}
        options={titleOptions}
        title="Title"
      />

      <DemoKeywordSuggestionModal
        visible={isKeywordModalVisible}
        onCancel={() => setIsKeywordModalVisible(false)}
        onSuggest={(lang, _opts) => {
          setIsKeywordModalVisible(false);
          setIsSuggestingKeywords(true);
          setTimeout(() => {
            setIsSuggestingKeywords(false);
            setSuggestedKeywordsReady((prev) => ({ ...prev, [lang]: true }));
            message.success('Demo: Keyword suggestions generated.');
          }, 3000);
        }}
        bookLanguage={book?.language || 'English'}
        initialTargetLanguage={selectedLanguage !== 'original' ? (getTargetLanguageFromPair(selectedLanguage) || '').trim() : undefined}
        genres={book?.genres || []}
        tropes={book?.tropes || []}
        keywords={book?.keywords || []}
        blurb={book?.blurb || ''}
        languages={['German', 'French']}
        resetCounter={resetCounter}
      />

      {/* Demo: Never/Always Translate Modals */}
      <DemoNeverTranslateModal
        isVisible={isNeverModalVisible}
        onClose={() => setIsNeverModalVisible(false)}
        currentLanguagePair={currentLanguagePair}
        neverTranslateWords={neverWords}
        setNeverTranslateWords={setNeverWords}
      />
      <DemoAlwaysTranslateModal
        isVisible={isAlwaysModalVisible}
        onClose={() => setIsAlwaysModalVisible(false)}
        currentLanguagePair={currentLanguagePair}
        alwaysTranslateWords={alwaysWords}
        setAlwaysTranslateWords={setAlwaysWords}
      />

      <DemoHumanReviewModal
        visible={humanReviewModalVisible}
        onClose={() => setHumanReviewModalVisible(false)}
      />
      <DemoTranslationModal
        visible={isTranslateModalVisible}
        onClose={() => setIsTranslateModalVisible(false)}
        bookLanguage={book?.language || 'English'}
        totalWords={pendingTranslateWords}
        scope={translateScope || 'book'}
        chapterOrder={selectedChapter?.order}
        translatedChaptersByLang={translatedChapters}
        chapters={chapters}
        languages={['German', 'French']}
        bookguid={bookguid}
        onTranslate={({ targetLanguage }) => {
          const target = targetLanguage;
          if (!target) {
            message.warning('Select a target language first.');
            return;
          }
          // Simulate translating
          setIsTranslateModalVisible(false);
          setIsTranslating(true);
          setTimeout(() => {
            setIsTranslating(false);
            if (translateScope === 'book') {
              const allOrders = chapters.map(c => c.order);
              setTranslatedChapters(prev => ({ ...prev, [target]: Array.from(new Set(allOrders)).sort((a,b)=>a-b) }));
              message.success('Demo: Book translated.');
            } else {
              if (selectedChapter) {
                const order = selectedChapter.order;
                setTranslatedChapters(prev => {
                  const list = new Set([...(prev[target] || [])]);
                  list.add(order);
                  return { ...prev, [target]: Array.from(list).sort((a,b)=>a-b) };
                });
                // Switch view to the newly translated language
                const origin = book?.language || 'English';
                setSelectedLanguage(`${origin}-${target}`);
                message.success('Demo: Chapter translated.');
              }
            }
            if (selectedChapter) void loadChapter(selectedChapter);
          }, 1500);
        }}
      />
      <DemoExportBookModal
        visible={isExportModalVisible}
        onClose={() => setIsExportModalVisible(false)}
        uploadType={(book?.uploadType || '').toLowerCase() === 'epub' ? 'ePub' : 'Word'}
        translationPairs={['English-German', 'English-French']}
        chapters={chapters}
        translatedChaptersByLang={translatedChapters}
        onExport={({ version, format, writingOrientation /*, includeImages */ }) => {
          void writingOrientation;
          try {
            const base = (() => {
              switch (bookguid) {
                case 'demo-book-flatland':
                  return 'Flatlands';
                case 'demo-book-time-machine':
                  return 'The_Time_Machine';
                default:
                  return (book?.title || 'Book').trim().replace(/\s+/g, '_');
              }
            })();

            const variant = version === 'Original' ? 'Original' : version; // e.g., 'English-German'
            const ext = format === 'ePub' ? 'epub' : 'docx';
            const filename = `${base}_${variant}.${ext}`;
            const url = `/demo/books/${bookguid}/${filename}`;

            // Trigger browser download
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.setAttribute('download', filename);
            anchor.style.display = 'none';
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);

            message.success('Demo: Export prepared. Your download should begin shortly.');
          } catch (e) {
            console.warn('Demo export failed', e);
            message.error('Demo: Failed to prepare export.');
          } finally {
            setIsExportModalVisible(false);
          }
        }}
      />
    </>
  );
};

export default DemoBookDetailPage;
